import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";
import { ProofForm } from "~/components/location-proof/ProofForm";
import { ApiError, locationProofScanQueryOptions } from "~/lib/api";

export const Route = createFileRoute("/(public)/laadpaal-locatie/$token")({
  component: LaadpaalLocatieRoute,
});

function PhoneShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background flex min-h-svh w-full justify-center px-4 py-8">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}

function Notice({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <h1 className="text-xl font-medium">{title}</h1>
      <p className="text-muted-foreground text-sm">{body}</p>
    </div>
  );
}

function LaadpaalLocatieRoute() {
  const { token } = Route.useParams();
  const { data, isLoading, error } = useQuery(locationProofScanQueryOptions(token));

  if (isLoading) {
    return (
      <PhoneShell>
        <div className="text-muted-foreground flex flex-col items-center gap-3 py-16">
          <LoaderCircle className="size-6 animate-spin" />
          <p className="text-sm">Bezig met laden…</p>
        </div>
      </PhoneShell>
    );
  }

  if (error || !data) {
    const expiredOrUsed = error instanceof ApiError && error.status === 410;
    return (
      <PhoneShell>
        <Notice
          title={expiredOrUsed ? "Deze link is niet meer geldig" : "Ongeldige link"}
          body={
            expiredOrUsed
              ? "Deze QR-code is verlopen of al gebruikt. Vraag op je computer een nieuwe QR-code aan."
              : "We konden deze link niet vinden. Scan de QR-code op je computer opnieuw."
          }
        />
      </PhoneShell>
    );
  }

  if (data.status !== "pending") {
    return (
      <PhoneShell>
        <Notice
          title={
            data.status === "submitted"
              ? "Deze laadpaal is al bevestigd"
              : "Deze link is verlopen"
          }
          body="Je kunt verdergaan op je computer."
        />
      </PhoneShell>
    );
  }

  return (
    <PhoneShell>
      <ProofForm
        token={token}
        chargerName={data.chargerName}
        chargerBrand={data.chargerBrand}
        chargerSerialNumber={data.chargerSerialNumber}
      />
    </PhoneShell>
  );
}
